import { ReactNode } from 'react';
import produce from 'immer';
import { colors } from '@styleseat/brand';

type SnackbarButtonAction = {
  text: string;
  action?: () => any;
};
export type DeprecatedIconClass = 'info-icon' | 'error-icon' | 'success-icon' | 'auto-checkout-icon' | 'sparkles-icon';

export type SnackbarState = {
  show: boolean;
  /** Used for titling a snackbar */
  header: string;
  /** Shown as a bolded start to a description */
  inlineHighlight: string;
  /**
   * When used with inlineHighlight, this is the remainder of the description that is not bold.
   * When used alone, this is simply the description text
   */
  subHeader: ReactNode;
  /** Props for the confirmation button text and onClick action */
  confirm: SnackbarButtonAction;
  /** Props for the dismiss button text and onClick action */
  dismiss: SnackbarButtonAction;
  /** @deprecated - Use sideContent to render icons */
  icon: DeprecatedIconClass | '';
  /**
   * If you'd like to replace the entire snackbar with a custom component,
   * use this together with mainContent/sideContent
   */
  removeFormatting?: boolean;
  /**
   * The left border color of the snackbar.
   * Set this to transparent or empty to remove the color
   */
  sideColor: string;
  /**
   * Custom rendered left side section. This replaces any `icon` passed in
   */
  sideContent: ReactNode;
  /**
   * Custom rendered main (right side) content.
   * This replaces the content that uses header, inlineHighlight, subHeader,
   * confirm, dismiss, and confirmWidget.
   */
  mainContent: ReactNode;
  /**
   * Custom rendered button section (displays beneath inlineHightlight/subHeader)
   */
  confirmWidget?: ReactNode;
  /**
   * Set an automatic time out period (in milliseconds) for dismissing the snackbar
   */
  timeOut?: number;
};

const defaultState = {
  show: false,
  header: '',
  inlineHighlight: '',
  subHeader: '',
  confirm: {
    text: '',
    action: () => { },
  },
  dismiss: {
    text: '',
    action: () => { },
  },
  /** @deprecated */
  icon: 'info-icon',
  sideColor: colors.slate,
  sideContent: null,
  mainContent: null,
  confirmWidget: null,
  timeOut: null,
};

const Snackbar = {
  state: defaultState as SnackbarState,

  reducers: {
    /**
     * Opens the snack bar
     */
    openSnackbar: produce((state: SnackbarState, payload): SnackbarState => ({
      ...state,
      ...payload,
      show: true,
    })),

    /**
     * Hides and resets the snackbar
     */
    closeSnackbar: produce((state: SnackbarState) => ({
      ...state,
      ...defaultState,
      show: false,
    })),
  },

  effects: (dispatch: any) => ({
    open: (payload: Partial<SnackbarState>, reducers: any) => {
      // if there is no current snackbar, open one
      if (!reducers.snackbar.show) {
        dispatch.snackbar.openSnackbar(payload);
      }
      if (payload?.timeOut) {
        setTimeout(() => dispatch.snackbar.closeSnackbar(), payload.timeOut);
      }
    },
    /**
     * Closes the snackbar with animations
     */
    close: () => {
      dispatch.snackbar.closeSnackbar();
    },
  }),
};

export default Snackbar;
